import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Clock, Lock, PlayCircle, BadgeCheck, Award, Loader2, Languages, FileText, ShieldCheck, ChevronDown, Layers, Gamepad2, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api, { formatApiError } from "../lib/api";
import { pick, pickList, catLabel, levelLabel } from "../lib/pick";
import { CertificatePreview } from "../components/CertificatePreview";
import useSeo from "../hooks/useSeo";

const Accordion = ({ open, onToggle, head, children, testid }) => (
  <div className={`card-dark overflow-hidden transition-colors duration-200 ${open ? "border-[#1D4ED8]" : ""}`} data-testid={testid}>
    <button type="button" onClick={onToggle} className="w-full flex items-center gap-4 p-5 text-left" data-testid={`${testid}-toggle`}>
      {head}
      <ChevronDown className={`w-5 h-5 text-[#1D4ED8] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
    <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
      <div className="px-5 pb-5 pt-0">{children}</div>
    </motion.div>
  </div>
);

export default function AcademyCourse() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [openLesson, setOpenLesson] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  const downloadManual = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/academy/courses/${slug}/manual`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-manual.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const applyCoupon = async () => {
    try {
      const { data: res } = await api.post("/academy/coupon/preview", { code: coupon, slug });
      setApplied(res);
      toast.success(t("academy.couponApplied"));
    } catch (e) {
      setApplied(null);
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    api.get(`/academy/courses/${slug}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [slug]);

  const buy = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setBuying(true);
    try {
      const { data: res } = await api.post("/academy/checkout", {
        slug,
        origin_url: window.location.origin,
        coupon_code: applied ? applied.code : "",
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBuying(false);
    }
  };

  useSeo({
    lang,
    title: data?.course ? pick(data.course, "title", lang) : t("academy.title"),
    description:
      (data?.course && (pick(data.course, "description", lang) || pick(data.course, "subtitle", lang))?.slice(0, 160)) ||
      (lang === "en"
        ? "Online sports science course with structured content, quizzes and certification."
        : "Online μάθημα αθλητικής επιστήμης με δομημένη ύλη, quiz και πιστοποίηση."),
    image: data?.course?.badge_image,
  });

  if (!data) return <p className="max-w-4xl mx-auto px-5 pt-20 text-[#64748B]">{t("common.loading")}</p>;

  const { course, lessons, has_access: hasAccess, certificate } = data;
  const outcomes = pickList(course, "outcomes", lang);
  const faqs = [1, 2, 3, 4, 5].map((n) => ({ q: t(`cfaq${n}.q`), a: t(`cfaq${n}.a`) }));
  const totalSlides = lessons.reduce((s, l) => s + (l.slides_count || 0), 0);
  const nextLesson = lessons.find((l) => !l.completed)?.id || lessons[0]?.id;
  const previewLesson = null;

  return (
    <>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 pt-8 pb-28 lg:pb-20" data-testid="academy-course-page">
        <Link data-testid="academy-course-back" to="/courses" className="inline-flex items-center gap-2 text-sm font-bold text-[#334155] hover:text-[#1D4ED8] transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> {t("academy.backToCatalog")}
        </Link>

        {/* HERO */}
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="panel-cream mt-6 overflow-hidden" data-testid="academy-course-hero">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-7 sm:p-10 lg:p-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">{catLabel(course.category, lang)} · {levelLabel(course.level, lang)}</p>
              <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-3 text-[#0B1B3A] leading-[1.05]" data-testid="academy-course-title">{pick(course, "title", lang)}</h1>
              {pick(course, "subtitle", lang) && <p className="text-base md:text-lg font-bold text-[#1D4ED8] mt-4">{pick(course, "subtitle", lang)}</p>}
              <p className="text-base text-[#475569] mt-4 leading-relaxed max-w-2xl">{pick(course, "description", lang)}</p>
              <div className="flex flex-wrap gap-2 mt-6" data-testid="academy-course-chips">
                {[
                  [Clock, `${course.hours}h ${t("academy.selfPaced")}`],
                  [Layers, course.includes?.length ? `${course.includes.length} ${t("path.badge")}` : `${course.lessons_count} ${t("learn.lessons")} · ${totalSlides} ${t("academy.slides")}`],
                  [Award, t("academy.certificateAt")],
                  course.bilingual && [Languages, t("academy.bilingual")],
                ].filter(Boolean).map(([Icon, label]) => (
                  <span key={label} className="inline-flex items-center gap-2 text-xs font-bold text-[#0B1B3A] bg-white border border-[#E2E8F0] rounded-full px-4 py-2"><Icon className="w-3.5 h-3.5 text-[#1D4ED8]" /> {label}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              {course.badge_image && <img src={course.badge_image} alt={course.title} className="w-full rounded-[24px] border-2 border-[#1D4ED8] shadow-[8px_8px_0_0_#1D4ED8] object-cover aspect-[4/3]" data-testid="academy-course-cover" />}
            </div>
          </div>
        </motion.section>

        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-10 mt-10">
          <div>
            {outcomes.length > 0 && (
              <section data-testid="academy-outcomes-section">
                <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("academy.whatYouLearn")}</h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5" data-testid="academy-outcomes">
                  {outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-3 card-dark p-4">
                      <span className="w-6 h-6 rounded-full bg-[#1D4ED8] text-white flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" /></span>
                      <span className="text-sm font-semibold text-[#1E293B]">{o}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.included_courses?.length > 0 && (
              <section className="mt-12" data-testid="path-included-section">
                <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("path.included")}</h2>
                <p className="text-sm text-[#475569] mt-2">{t("path.includedSub")}</p>
                <div className="mt-5 grid sm:grid-cols-2 gap-4">
                  {data.included_courses.map((ic, i) => (
                    <Link key={ic.id} to={`/academy/${ic.slug}`} className="card-3d overflow-hidden flex flex-col" data-testid={`path-course-${ic.slug}`}>
                      {ic.badge_image && <img src={ic.badge_image} alt={ic.title} className="w-full h-32 object-cover" />}
                      <div className="p-5 flex-1 flex flex-col">
                        <span className="badge-outline w-fit">{i + 1}/{data.included_courses.length}</span>
                        <h3 className="font-heading font-black text-base text-[#0B1B3A] mt-3">{pick(ic, "title", lang)}</h3>
                        <p className="text-xs text-[#64748B] mt-2">{ic.lessons_count} {t("learn.lessons")} · {ic.hours}h · <span className="line-through">{ic.price}€</span></p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {lessons.length > 0 && (
            <section className="mt-12" data-testid="academy-syllabus-section">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("academy.syllabus")}</h2>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">{course.lessons_count} {t("learn.lessons")} · {totalSlides} {t("academy.slides")}</p>
              </div>
              <div className="mt-5 space-y-3" data-testid="academy-syllabus">
                {lessons.map((l, i) => (
                  <Accordion
                    key={l.id}
                    testid={`academy-lesson-row-${l.id}`}
                    open={openLesson === i}
                    onToggle={() => setOpenLesson(openLesson === i ? -1 : i)}
                    head={
                      <>
                        <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-heading font-black text-sm ${l.completed ? "bg-[#1D4ED8] text-white" : "bg-[#EEF4FF] text-[#1D4ED8]"}`}>
                          {l.completed ? <Check className="w-4 h-4" /> : i + 1}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-bold text-[#0B1B3A] truncate">{pick(l, "title", lang)}</span>
                          <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#64748B] mt-1">
                            {l.slides_count} {t("academy.slides")} · {l.xp} XP{l.locked ? ` · ${t("academy.locked")}` : ""}
                          </span>
                        </span>
                        {l.locked ? <Lock className="w-4 h-4 text-[#94A3B8] shrink-0" /> : <PlayCircle className="w-4 h-4 text-[#1D4ED8] shrink-0" />}
                      </>
                    }
                  >
                    {pick(l, "summary", lang) && <p className="text-sm text-[#475569] leading-relaxed">{pick(l, "summary", lang)}</p>}
                    {!l.locked && (
                      <Link data-testid={`academy-lesson-open-${l.id}`} to={`/academy/lesson/${l.id}`} className="btn-blue !px-5 !py-2 text-xs mt-4 inline-flex">
                        {l.completed ? t("learn.review") : t("learn.start")} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </Accordion>
                ))}
              </div>
            </section>
            )}

            <CertificatePreview title={course.title} hours={course.hours} />
            <div className="card-dark p-6 mt-6 flex flex-wrap items-center gap-6" data-testid="academy-course-badge-preview">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/badges/course/${course.id}.png`} alt="" className="w-32 h-32 object-contain" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">{t("badges.youEarn")}</p>
                <p className="font-heading font-black text-xl text-[#0B1B3A] mt-1">{t("badges.courseBadge")}</p>
                <p className="text-sm text-[#475569] mt-2">{t("badges.courseBadgeDesc")}</p>
              </div>
            </div>

            <section className="mt-12" data-testid="academy-faq-section">
              <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("academy.faq")}</h2>
              <div className="mt-5 space-y-3">
                {faqs.map((f, i) => (
                  <Accordion key={i} testid={`academy-faq-${i}`} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} head={<span className="flex-1 font-bold text-[#0B1B3A]">{f.q}</span>}>
                    <p className="text-sm text-[#475569] leading-relaxed">{f.a}</p>
                  </Accordion>
                ))}
              </div>
            </section>
          </div>

          <motion.aside initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="card-dark p-7 h-fit lg:sticky lg:top-24 border-2 border-[#1D4ED8]/20" data-testid="academy-purchase-panel">
            {course.is_free ? (
              <p className="font-heading font-black text-3xl text-[#0B1B3A]">{t("academy.free")}</p>
            ) : (
              <>
                {course.offer_price ? (
                  <>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#1D4ED8]" data-testid="academy-course-offer">
                      {(lang === "en" ? course.offer_label_en : course.offer_label)} · −{course.offer_pct}%
                    </p>
                    <p className="text-sm text-[#94A3B8] line-through">{course.price}€</p>
                  </>
                ) : (
                  course.price_original > course.price && (
                    <p className="text-sm text-[#94A3B8] line-through">{course.price_original}€</p>
                  )
                )}
                <p className="font-heading font-black text-4xl text-[#0B1B3A]" data-testid="academy-course-price">
                  {applied ? applied.final_price : (course.offer_price || course.price)}€
                </p>
                {applied && (
                  <p className="text-xs font-bold text-[#16A34A] mt-1" data-testid="academy-course-discount">
                    -{applied.discount}€ ({applied.code})
                  </p>
                )}
                <p className="text-xs text-[#64748B] mt-2">{t("academy.oneTime")}</p>
              </>
            )}

            <ul className="mt-6 space-y-2.5 text-sm text-[#334155]">
              <li className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#1D4ED8]" /> {course.hours}h {t("academy.selfPaced")}</li>
              <li className="inline-flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#1D4ED8]" /> {course.includes?.length ? `${course.includes.length} ${t("path.badge")}` : `${course.lessons_count} ${t("learn.lessons")}`}</li>
              <li className="inline-flex items-center gap-2"><Award className="w-4 h-4 text-[#1D4ED8]" /> {t("academy.certificateAt")}</li>
              <li className="inline-flex items-center gap-2" data-testid="academy-own-cert-badge"><ShieldCheck className="w-4 h-4 text-[#1D4ED8]" /> {t("academy.ownCert")}</li>
              {course.has_manual && (
                <li className="inline-flex items-center gap-2"><FileText className="w-4 h-4 text-[#1D4ED8]" /> {t("academy.manualIncluded")}</li>
              )}
            </ul>

            {hasAccess ? (
              <div className="mt-7 space-y-3">
                <p className="text-sm font-bold text-[#1D4ED8]" data-testid="academy-has-access">{t("academy.youHaveAccess")}</p>
                {course.has_manual && (
                  <button
                    data-testid="academy-manual-download"
                    onClick={downloadManual}
                    disabled={downloading}
                    className="btn-outline-light w-full justify-center disabled:opacity-60"
                  >
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} {t("academy.downloadManual")}
                  </button>
                )}
                {lessons[0] && (
                  <Link data-testid="academy-start-button" to={`/academy/lesson/${nextLesson}`} className="btn-blue w-full justify-center">
                    {t("academy.continue")} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {course.final_quiz_size > 0 && (
                  <Link data-testid="academy-quiz-button" to={`/academy/${slug}/quiz`} className="btn-outline-light w-full justify-center">
                    {t("academy.finalQuiz")}
                  </Link>
                )}
                {certificate && (
                  <p className="text-xs text-[#16A34A] font-bold" data-testid="academy-certificate-note">
                    {t("academy.certificateIssued")} · {certificate.code}
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-7 space-y-3">
                <div className="flex gap-2">
                  <input
                    data-testid="academy-coupon-input"
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    placeholder={t("academy.couponPlaceholder")}
                    className="input-flat !py-2.5 text-sm"
                  />
                  <button data-testid="academy-coupon-apply" type="button" onClick={applyCoupon} disabled={!coupon} className="btn-outline-light !px-4 !py-2.5 text-xs disabled:opacity-50">
                    {t("academy.couponApply")}
                  </button>
                </div>
                <button data-testid="academy-buy-button" onClick={buy} disabled={buying} className="btn-blue w-full justify-center disabled:opacity-60">
                  {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t("academy.buyNow")}
                </button>
                <p className="text-xs text-[#64748B]">{t("academy.paymentNote")}</p>
                {previewLesson && (
                  <Link data-testid="academy-preview-button" to={`/academy/lesson/${previewLesson.id}`} className="btn-outline-light w-full justify-center">
                    {t("academy.tryPreview")}
                  </Link>
                )}
              </div>
            )}
          </motion.aside>
        </div>
      </div>

      {/* Mobile sticky bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#E2E8F0] px-5 py-3 flex items-center gap-4" data-testid="academy-mobile-bar">
        <div className="flex-1 min-w-0">
          <p className="font-heading font-black text-xl text-[#0B1B3A]">{course.is_free ? t("academy.free") : `${applied ? applied.final_price : course.price}€`}</p>
          <p className="text-[11px] text-[#64748B] truncate">{pick(course, "title", lang)}</p>
        </div>
        {hasAccess ? (
          <Link to={`/academy/lesson/${nextLesson}`} className="btn-blue !px-5 !py-2.5 text-sm" data-testid="academy-mobile-continue">{t("academy.continue")}</Link>
        ) : (
          <button onClick={buy} disabled={buying} className="btn-blue !px-5 !py-2.5 text-sm disabled:opacity-60" data-testid="academy-mobile-buy">{t("academy.buyNow")}</button>
        )}
      </div>
      <Footer />
    </>
  );
}
